import PropTypes from 'prop-types'
import React, { memo, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getJoistState } from '../../../../../store/selectors'
import { joistActions } from '../../../../../store/slices'

import {
  Accordion,
  ButtonGroup,
  Checkbox,
  Dropdown,
  FormControl,
  FormGroup,
  FormLabel,
} from '../../../../../../../components'
import CommonSelector from './CommonSelector'
import IJoistSelector from './IJoistSelector'
import MemberLoads from './MemberLoads'

import {
  CARRIED_MEMBER,
  IJOIST_INPUT_OPTIONS,
  JOIST_TYPE_LABEL,
} from '../../../../../common/constants'
import {
  getDefaultOptValue,
  getDefaultPlyValue,
  getGAValue,
  shouldDisplayNumberOfPlies,
} from '../../../../../common/fns'
import { useCalcActions, useCommonOptions } from '../../../../../common/hooks'
import { joist as joistData } from '../../../../../common/json/carried-member.json'

import { IJOIST_INPUT_TYPE } from '../../../../../../../utils/constants'
import { RenderIf, isNull, isOneDropdownOption } from '../../../../../../../utils/fns'

const label = CARRIED_MEMBER.JOIST

const Joist = (props) => {
  const { country } = props

  const dispatch = useDispatch()
  const state = useSelector(getJoistState)
  const { pliesOptions, ...optionsData } = useCommonOptions(state, country, joistData)

  const { errors, validateField, setCalcValue, updateCalcValue } = useCalcActions(joistActions)

  const handleChangeMemberType = useCallback((e) => {
    updateCalcValue({ ...joistData.defaultValue[e.target.value] })
  }, [])

  useEffect(() => {
    const isWidthExists = optionsData.widthOptions.some((item) => item.value === state.width)

    if (!isWidthExists && optionsData.widthOptions.length > 0) {
      const value = getDefaultOptValue(optionsData.widthOptions)
      dispatch(joistActions.modifyCalculation({ name: 'width', value }))
    }
  }, [state.material, optionsData.widthOptions])

  useEffect(() => {
    const isDepthExists = optionsData.depthOptions.some((item) => item.value === state.depth)

    if (!isDepthExists && optionsData.depthOptions.length > 0) {
      const value = getDefaultOptValue(optionsData.depthOptions)
      dispatch(joistActions.modifyCalculation({ name: 'depth', value }))
    }
  }, [state.width, optionsData.depthOptions])

  useEffect(() => {
    const isPlyExists = pliesOptions.some((item) => item.value === state.ply)

    if (!isPlyExists || !shouldDisplayNumberOfPlies(state.type, pliesOptions, state.isRough)) {
      dispatch(
        joistActions.modifyCalculation({ name: 'ply', value: getDefaultPlyValue(pliesOptions) }),
      )
    }
  }, [state.depth, state.isRough, state.type, state.ply, pliesOptions])

  useEffect(() => {
    const isThicknessExists = optionsData.thicknessOptions.some(
      (item) => item.value === state.thickness,
    )

    if (!isThicknessExists && optionsData.thicknessOptions.length > 0) {
      const value = getDefaultOptValue(optionsData.thicknessOptions)
      dispatch(joistActions.modifyCalculation({ name: 'thickness', value }))
    }
  }, [state.material, optionsData.thicknessOptions])

  return (
    <Accordion title="Joist (Carried Member)">
      {state.type === 'ijoist' && (
        <ButtonGroup
          className="row"
          colClassName="col-6"
          name="ijoistInputType"
          options={IJOIST_INPUT_OPTIONS}
          value={state.ijoistInputType}
          onChange={(name, value) => updateCalcValue({ [name]: value })}
          containerProps={{ 'data-testid': 'joist_ijoistInputType' }}
        />
      )}

      <div className="grid grid__auto--250 grid--gap-1-2">
        <FormGroup>
          <FormLabel htmlFor="type">Member Type</FormLabel>
          <Dropdown
            name="type"
            value={state.type}
            options={joistData.memberTypeData}
            onChange={handleChangeMemberType}
            data-ga4={`joist_memberType:${JOIST_TYPE_LABEL[state.type]}`}
            data-testid="joist_type"
          />
        </FormGroup>

        {state.type === 'ijoist' &&
        // (mode === 0 )
        state.ijoistInputType === IJOIST_INPUT_TYPE.MANUFACTURER ? (
          <IJoistSelector
            label={label}
            material={state.material}
            manufacturer={state.manufacturer}
            series={state.series}
            model={state.model}
            width={state.width}
            onChange={updateCalcValue}
          />
        ) : (
          <CommonSelector
            label={label}
            type={state.type}
            material={state.material}
            width={state.width}
            depth={state.depth}
            thickness={state.thickness}
            onChange={setCalcValue}
            optionsData={optionsData}
          />
        )}

        {shouldDisplayNumberOfPlies(state.type, pliesOptions, state.isRough) && (
          <FormGroup>
            <FormLabel htmlFor="ply">Number of Plies</FormLabel>
            <Dropdown
              name="ply"
              value={state.ply}
              options={pliesOptions}
              onChange={setCalcValue}
              disabled={isOneDropdownOption(pliesOptions)}
              data-ga4={`joist_ply:${getGAValue(pliesOptions, state.ply)}`}
              data-testid="ply"
            />
          </FormGroup>
        )}

        <FormGroup>
          <FormLabel htmlFor="joist.memberID">Member ID</FormLabel>
          <FormControl
            name="joist.memberID"
            value={state.memberID}
            onChange={(e) => {
              validateField(e)
              setCalcValue(e)
            }}
            error={errors['joist.memberID']}
            data-testid="joist.memberID"
          />
        </FormGroup>

        <MemberLoads
          label={label}
          country={country}
          load={state.load}
          uplift={state.uplift}
          onChange={setCalcValue}
          onValidateField={validateField}
          errors={errors}
        />

        {RenderIf(
          !isNull(state.isRough),
          <FormGroup>
            <FormLabel>Lumber Finish</FormLabel>
            <Checkbox
              name="isRough"
              label="Rough Sawn"
              checked={state.isRough}
              onChange={(e) =>
                dispatch(
                  joistActions.modifyCalculation({ name: e.target.name, value: e.target.checked }),
                )
              }
              data-ga4={`joist_isRough:${state.isRough}`}
              data-testid="isRough"
            />
          </FormGroup>,
        )}
      </div>
    </Accordion>
  )
}

Joist.propTypes = {
  country: PropTypes.string.isRequired,
}

export default memo(Joist)
